import { useEffect, useState } from "react";

interface AsyncState<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | undefined;
}

interface UseAsyncOptions {
  cacheKey?: string;
  cacheTTL?: number;
}

const cache = new Map<string, { data: unknown; ts: number }>();

export function useAsync<T>(
  fn: () => Promise<T>,
  deps: React.DependencyList = [],
  opts?: UseAsyncOptions,
) {
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<AsyncState<T>>(() => {
    if (opts?.cacheKey) {
      const cached = cache.get(opts.cacheKey);
      if (cached && Date.now() - cached.ts < (opts.cacheTTL ?? 60_000)) {
        return { data: cached.data as T, loading: false, error: undefined };
      }
    }
    return { data: undefined, loading: true, error: undefined };
  });

  useEffect(() => {
    let cancelled = false;

    if (opts?.cacheKey) {
      const cached = cache.get(opts.cacheKey);
      if (cached && Date.now() - cached.ts < (opts.cacheTTL ?? 60_000)) {
        setState({ data: cached.data as T, loading: false, error: undefined });
        return;
      }
    }

    setState((s) => ({ ...s, loading: true, error: undefined }));
    fn()
      .then((data) => {
        if (opts?.cacheKey) {
          cache.set(opts.cacheKey, { data, ts: Date.now() });
        }
        if (!cancelled) setState({ data, loading: false, error: undefined });
      })
      .catch((error: Error) => {
        if (!cancelled) setState({ data: undefined, loading: false, error });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { ...state, reload: () => setTick((n) => n + 1) };
}
